import { ICartEntry, IItem } from '@rbi-ctg/menu';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { createCartEntry } from 'utils/cart';

type useDonationProps = {
  donationItems: IItem[];
};

function useDonation({ donationItems }: useDonationProps) {
  const {
    cartEntries,
    addToCart,
    removeFromCart,
  }: {
    cartEntries: ICartEntry[];
    addToCart: (newCartEntry: ICartEntry) => void;
    removeFromCart: ({ cartId }: { cartId: string }) => void;
  } = useOrderContext();

  const { pricingFunction } = useMenuContext();

  const getPreloadedDonationCartEntry = () => {
    const preloadedDonationCartEntry = cartEntries.find(cartEntry => cartEntry.isDonation);
    if (preloadedDonationCartEntry && donationItems) {
      const isPreloadedDonationCartEntryValid = donationItems.some(
        item => item._id === preloadedDonationCartEntry._id
      );
      if (isPreloadedDonationCartEntryValid) {
        return preloadedDonationCartEntry;
      } else {
        removeFromCart({ cartId: preloadedDonationCartEntry.cartId });
      }
    }
    return null;
  };

  const selectedDonationCartEntry = getPreloadedDonationCartEntry();

  function handleAddDonationCartEntry(donationItem: IItem) {
    if (selectedDonationCartEntry?._id === donationItem._id) {
      return;
    }

    if (selectedDonationCartEntry) {
      removeFromCart({ cartId: selectedDonationCartEntry.cartId });
    }

    const newDonationCartEntry: ICartEntry = {
      ...createCartEntry({
        item: {
          ...donationItem,
        },
        price: pricingFunction(donationItem),
      }),
      isDonation: true,
    };
    addToCart(newDonationCartEntry);
  }

  function handleRemoveDonationCartEntry({ cartId }: { cartId: string }) {
    removeFromCart({ cartId });
  }

  function handleOnEnableDonationChange(isEnabled: boolean) {
    if (isEnabled) {
      const defaultDonationItem = donationItems[0];
      handleAddDonationCartEntry(defaultDonationItem);
    } else {
      if (selectedDonationCartEntry) {
        handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry?.cartId });
      }
    }
  }

  return {
    selectedDonationCartEntry,
    isDonationEnabled: !!selectedDonationCartEntry,
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
    handleOnEnableDonationChange,
  };
}

export default useDonation;
